import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BroadcastChannel } from 'broadcast-channel';
import { Settings } from 'luxon';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import App from './App.js';
import Auth0ProviderWithHistory from './auth/auth0-provider-with-history.js';
import ConsecutiveSnackbars from './components/ConsecutiveSnackbars.js';
import BroadcastChannelProvider from './providers/BroadcastChannel.js';
import store from './redux/store.js';
import reportWebVitals from './reportWebVitals.js';
import { initAnalytics } from './utils/index.js';


// https://sentry.io/onboarding/onekey-virtual-care/get-started/

// TODO - Integrate Sentry with React Router v6, when support is available:
// https://github.com/getsentry/sentry-javascript/issues/4118
// Hopefully this includes suppoort for 404 capturing
Settings.defaultLocale = 'en-US';
Sentry.init({
  dsn: 'https://c0c047f878b2418792572ff805fd0249@o1165368.ingest.sentry.io/6255040',
  integrations: [new BrowserTracing()],
  environment: import.meta.env.VITE_ENV,
  enabled: import.meta.env.VITE_ENV !== 'dev',
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

const appElement = document.getElementById('root');
const broadcastChannel = new BroadcastChannel('care-center');
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

initAnalytics();

const root = createRoot(appElement as HTMLDivElement); // createRoot(container!) if you use TypeScript

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Auth0ProviderWithHistory>
          <BroadcastChannelProvider broadcastChannel={broadcastChannel}>
            <Provider store={store}>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <App />
                <ConsecutiveSnackbars />
              </LocalizationProvider>
            </Provider>
          </BroadcastChannelProvider>
        </Auth0ProviderWithHistory>
      </BrowserRouter>

      <ReactQueryDevtools initialIsOpen={false} position={'bottom-right'}/>
    </QueryClientProvider>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
